/** Matches Laravel `localStorage` key and `app.blade.php` inline script. */
export const THEME_STORAGE_KEY = 'theme';

export const THEME_CHANGE_EVENT = 'buyme-theme-changed';
export const THEME_COOKIE_KEY = 'theme';

export type ThemeValue = 'light' | 'dark';

function parseTheme(raw: string | null | undefined): ThemeValue | null {
  return raw === 'dark' || raw === 'light' ? raw : null;
}

export function getThemeFromCookie(): ThemeValue | null {
  if (typeof document === 'undefined') return null;
  const part = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${THEME_COOKIE_KEY}=`));
  if (!part) return null;
  const raw = decodeURIComponent(part.split('=').slice(1).join('='));
  return parseTheme(raw);
}

export function getPersistedTheme(): ThemeValue | null {
  if (typeof window === 'undefined') return null;
  const cookieTheme = getThemeFromCookie();
  if (cookieTheme) return cookieTheme;
  try {
    return parseTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistTheme(theme: ThemeValue): void {
  if (typeof document === 'undefined') return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore storage failures
  }
  // Shared across localhost ports so Next/Laravel stay in sync.
  document.cookie = `${THEME_COOKIE_KEY}=${encodeURIComponent(theme)}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

/**
 * Append `?theme=light|dark` only when the link leaves the current origin (e.g. Next on :3000 →
 * Laravel on :8000). Same host + `Path=/` theme cookie already keeps storefront + `/api` in sync
 * without polluting shareable listing URLs.
 */
export function withThemeQuery(url: string): string {
  if (typeof window === 'undefined') return url;
  const stored = getPersistedTheme();
  if (stored !== 'dark' && stored !== 'light') return url;
  try {
    const u = new URL(url, window.location.href);
    if (u.origin === window.location.origin) {
      return url;
    }
    u.searchParams.set('theme', stored);
    return u.toString();
  } catch {
    return url;
  }
}

export function dispatchThemeChanged(theme: ThemeValue): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { theme } }));
}
